import React, { useState } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faSearch } from '@fortawesome/free-solid-svg-icons';

const FAQContainer = styled.div`
  padding: 4rem 8rem;
  max-width: 1400px;
  margin: 0 auto;
  
  @media (max-width: 1200px) {
    padding: 4rem;
  }
  
  @media (max-width: 768px) {
    padding: 2rem;
  }
`;

const FAQSection = styled.div`
  margin-bottom: 3rem;
`;

const FAQGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FAQItem = styled.div`
  background: rgba(10, 10, 10, 0.8);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.05);
  transition: transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease;
  width: 100%;
  
  &:hover {
    transform: translateY(-2px);
    border-color: var(--primary-color);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.4);
  }
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const PageTitle = styled.h1`
  font-size: 3.5rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  text-align: center;
  background: linear-gradient(45deg, var(--primary-color), rgba(255, 51, 51, 0.7));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-shadow: 0 0 30px rgba(255, 51, 51, 0.3);
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const PageSubtitle = styled.p`
  color: #aaa;
  text-align: center;
  max-width: 700px;
  margin: 0 auto 3rem;
  font-family: 'Poppins', sans-serif;
  line-height: 1.6;
  
  &::after {
    content: '';
    display: block;
    width: 80px;
    height: 3px;
    background: var(--primary-color);
    margin: 20px auto 0;
  }
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const SearchContainer = styled.div`
  position: relative;
  max-width: 600px;
  margin: 0 auto 3rem;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 1rem 1rem 1rem 3rem;
  background: rgba(10, 10, 10, 0.8);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 16px;
  color: #fff;
  font-family: 'Poppins', sans-serif;
  font-size: 1rem;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 20px rgba(255, 51, 51, 0.2);
  }
  
  &::placeholder {
    color: #666;
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: #666;
`;

const SectionTitle = styled.h2`
  color: #fff;
  margin-bottom: 2rem;
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
  font-size: 1.8rem;
  background: linear-gradient(45deg, var(--primary-color), rgba(255, 51, 51, 0.7));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const FAQQuestionWrapper = styled.div`
  display: flex;
  justify-content: center;
`;

const FAQQuestion = styled.div`
  padding: 1.5rem;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  overflow: visible;
  
  &:hover {
    background: rgba(255, 255, 255, 0.02);
  }
`;

const QuestionText = styled.h3`
  flex: 1;
  color: #fff;
  font-family: 'Poppins', sans-serif;
  font-weight: 500;
  margin: 0;
  font-size: 1.1rem;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const FAQAnswer = styled.div`
  max-height: 0;
  opacity: 0;
  overflow: hidden;
  transition: max-height 0.3s ease, opacity 0.3s ease;
  color: #aaa;
  font-family: 'Poppins', sans-serif;
  line-height: 1.6;
  border-top: 1px solid rgba(255, 51, 51, 0.1);
  margin-top: 0; 
  padding: 0 1.5rem;
`;

const IconWrapper = styled.div`
  color: var(--primary-color);
  transition: transform 0.3s ease;
  transform: ${props => props.isOpen ? 'rotate(180deg)' : 'rotate(0)'};
`;

const NoResults = styled.div`
  text-align: center;
  color: #aaa;
  font-family: 'Poppins', sans-serif;
  padding: 2rem;
  background: rgba(10, 10, 10, 0.8);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.05);
`;

const DiscordLink = styled.div`
  text-align: center;
  margin-top: 3rem;
  color: #aaa;
  font-family: 'Poppins', sans-serif;
  
  a {
    color: var(--primary-color);
    text-decoration: none;
    font-weight: 600;
    transition: all 0.3s ease;
    
    &:hover {
      color: #fff;
      text-shadow: 0 0 10px var(--primary-color);
    }
  }
`;

const FAQ = () => {
  const [activeIndex, setActiveIndex] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  const faqData = {
    general: [
      {
        question: "What is LAVA Minecraft server?",
        answer: "LAVA is a unique Minecraft server featuring a cyberpunk-themed world with custom gameplay mechanics, special events, and a friendly community. Our server offers various game modes and features that you won't find anywhere else."
      },
      {
        question: "What version of Minecraft do I need to play?",
        answer: "Our server runs on Minecraft Java Edition version 1.19.2. Make sure your client is updated to this version to join our server."
      },
      {
        question: "Is the server free to play?",
        answer: "Yes! LAVA is completely free to play. While we offer optional purchases in our store to support the server, all core gameplay features are available to everyone."
      }
    ],
    gameplay: [
      {
        question: "What game modes are available on LAVA?",
        answer: "LAVA offers several game modes including Survival, Creative, SkyBlock, and our unique LavaSurvival mode where players must navigate dangerous lava-filled landscapes."
      },
      {
        question: "Are there any custom plugins or features?",
        answer: "Yes! We have numerous custom plugins including enhanced crafting, special abilities, custom mobs, and unique events that happen regularly throughout the server."
      },
      {
        question: "How do I claim land or protect my builds?",
        answer: "We use a land claiming system that allows you to protect your builds. Simply use the /claim command while standing in the area you want to protect, and follow the instructions."
      }
    ],
    technical: [
      {
        question: "I'm experiencing lag, what can I do?",
        answer: "First, check your own internet connection. If the issue persists, try reducing your render distance and graphics settings. If you're still experiencing lag, please contact our support team on Discord."
      },
      {
        question: "How do I report bugs or issues?",
        answer: "You can report bugs or issues on our Discord server in the #bug-reports channel. Please provide as much detail as possible, including screenshots if applicable."
      },
      {
        question: "Can I use mods or texture packs on the server?",
        answer: "We allow most client-side mods and all texture packs. However, any mods that give an unfair advantage (such as X-ray or auto-clickers) are strictly prohibited and may result in a ban."
      }
    ],
    economy: [
      {
        question: "How does the economy work on LAVA?",
        answer: "LAVA has a player-driven economy where you can buy and sell items with other players. We use a currency system with both physical and virtual money."
      },
      {
        question: "How do I earn money on the server?",
        answer: "You can earn money by selling items to other players, completing jobs, participating in events, and voting for our server daily."
      },
      {
        question: "Are there player shops or a marketplace?",
        answer: "Yes! We have a central marketplace where you can set up your own shop, as well as a global auction house where you can list items for sale."
      }
    ]
  };

  const toggleFAQ = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  const filterFAQs = () => {
    const filteredFAQs = {};
    Object.keys(faqData).forEach(category => {
      const filteredCategory = faqData[category].filter(item => 
        item.question.toLowerCase().includes(searchTerm.toLowerCase()) || 
        item.answer.toLowerCase().includes(searchTerm.toLowerCase())
      );
      if (filteredCategory.length > 0) {
        filteredFAQs[category] = filteredCategory;
      }
    });
    return filteredFAQs;
  };

  const filteredFAQs = searchTerm ? filterFAQs() : faqData;

  return (
    <FAQContainer>
      <PageTitle>FREQUENTLY ASKED QUESTIONS</PageTitle>
      <PageSubtitle>
        Find answers to common questions about our server, gameplay mechanics, and community guidelines.
      </PageSubtitle>

      <SearchContainer>
        <SearchIcon>
          <FontAwesomeIcon icon={faSearch} />
        </SearchIcon>
        <SearchInput 
          type="text" 
          placeholder="Search for a question..." 
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </SearchContainer>

      {Object.keys(filteredFAQs).length === 0 ? (
        <NoResults>
          No results found for "{searchTerm}". Try a different search term.
        </NoResults>
      ) : (
        Object.entries(filteredFAQs).map(([category, questions], categoryIndex) => (
          <FAQSection key={categoryIndex}>
            <SectionTitle>
              {category.charAt(0).toUpperCase() + category.slice(1)} Questions
            </SectionTitle>
            <FAQGrid>
              {questions.map((faq, index) => (
                <FAQItem key={index}>
                  <FAQQuestionWrapper>
                    <FAQQuestion onClick={() => toggleFAQ(`${category}-${index}`)}>
                      <QuestionText>{faq.question}</QuestionText>
                      <IconWrapper isOpen={activeIndex === `${category}-${index}`}>
                        <FontAwesomeIcon icon={faChevronDown} />
                      </IconWrapper>
                    </FAQQuestion>
                  </FAQQuestionWrapper>
                  {activeIndex === `${category}-${index}` && (
                    <FAQAnswer style={{ maxHeight: '300px', opacity: 1, paddingBottom: '1.5rem' }}>
                      {faq.answer}
                    </FAQAnswer>
                  )}
                </FAQItem>
              ))}
            </FAQGrid>
          </FAQSection>
        ))
      )}

      <DiscordLink>
        <p>
          Can't find what you're looking for? Join our <a href="https://discord.gg/your-discord-link" target="_blank" rel="noopener noreferrer">Discord</a> for more help!
        </p>
      </DiscordLink>
    </FAQContainer>
  );
};

export default FAQ;
